import React from "react";
import ReferenceExampleLink from "./ReferenceExampleLink";
import "./CodexReferenceSummary.css";

const score = (value) => value == null ? "n/a" : `${value}/100`;
const normalized = (value) => String(value || "").toUpperCase();
const ALIGNMENTS = ["SUPPORTS", "CONFLICTS", "NEUTRAL"];
const AGREEMENT_LABELS = {
  AGREE: "Chart and references agree",
  DISAGREE: "Chart and references disagree",
  MIXED: "Chart and references give mixed signals",
};

export function hasStructuredReferenceAssessment(evaluation) {
  return evaluation?.responseMode === "REFERENCE_V1"
    && ALIGNMENTS.includes(normalized(evaluation.chartAlignment))
    && ALIGNMENTS.includes(normalized(evaluation.referenceAlignment))
    && Object.prototype.hasOwnProperty.call(AGREEMENT_LABELS, normalized(evaluation.assessmentAgreement))
    && [evaluation.chartEvidence, evaluation.referenceEvidence].every((items) => Array.isArray(items)
      && items.length > 0 && items.every((item) => typeof item === "string" && item.trim()))
    && typeof evaluation.conclusion === "string" && evaluation.conclusion.trim().length > 0;
}

function Alignment({ value }) {
  const alignment = normalized(value);
  return <strong className={`codexReferenceAlignment codexReferenceAlignment${alignment}`}>{alignment}</strong>;
}

function Evidence({ items }) {
  return <ul className="codexReferenceEvidence">{items.map((item, index) => <li key={index}>{item}</li>)}</ul>;
}

export default function CodexReferenceSummary({ evaluation, detailed = false }) {
  if (evaluation?.requestedMode === "REFERENCE" && evaluation.effectiveMode === "LIGHT") {
    const reason = typeof evaluation.fallbackReason === "string" ? evaluation.fallbackReason.slice(0, 500).trim() : "";
    return <section className="codexReportSection" aria-label="Reference fallback">
      <strong>Reference unavailable — assessed with LIGHT</strong>
      {reason && <p>{reason}</p>}
    </section>;
  }
  if (evaluation?.responseMode !== "REFERENCE_V1") return null;
  const match = (slot, pattern) => pattern.test(slot || "")
    ? <ReferenceExampleLink key={`${evaluation.referencePackId}:${evaluation.referenceVersion}:${slot}`}
      packId={evaluation.referencePackId} version={evaluation.referenceVersion} slot={slot} />
    : "No matching slot";
  const similarities = <div className="codexEvaluationHeader">
      <span>Successful similarity {score(evaluation.successScore)} · {match(evaluation.matchedSuccessSlot, /^S[123]$/)}</span>
      <span>Failed similarity {score(evaluation.failureScore)} · {match(evaluation.matchedFailureSlot, /^F[123]$/)}</span>
    </div>;
  const pack = detailed && <dl className="codexReportDefinitionList">
      <dt>Reference pack</dt><dd>{evaluation.referencePackId || "n/a"} · version {evaluation.referenceVersion ?? "n/a"}</dd>
    </dl>;
  if (hasStructuredReferenceAssessment(evaluation)) {
    const agreement = normalized(evaluation.assessmentAgreement);
    return <div className="codexReferenceAssessment" aria-label="Reference assessment">
      <section className="codexReportSection" aria-label="Current chart">
        <div className="codexReferenceSectionHeading"><h4>Current chart</h4><Alignment value={evaluation.chartAlignment} /></div>
        <Evidence items={evaluation.chartEvidence} />
      </section>
      <section className="codexReportSection" aria-label="Reference comparison">
        <div className="codexReferenceSectionHeading"><h4>Reference comparison</h4><Alignment value={evaluation.referenceAlignment} /></div>
        {similarities}
        <Evidence items={evaluation.referenceEvidence} />
        {pack}
      </section>
      <section className="codexReportSection" aria-label="Final conclusion">
        <div className="codexReferenceSectionHeading"><h4>Final conclusion</h4><Alignment value={evaluation.eventAlignment} /></div>
        <p className={`codexReferenceAgreement codexReferenceAgreement${agreement}`}>{AGREEMENT_LABELS[agreement]}</p>
        <p className="codexReferenceConclusion">{evaluation.conclusion}</p>
      </section>
    </div>;
  }
  return <section className="codexReportSection" aria-label="Reference comparison">
    {similarities}
    {pack}
  </section>;
}
